import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AuthDataService } from '@nomoh-monorepo/data-access-auth';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  PessoaDataService,
  ServicoDataService,
} from '@nomoh-monorepo/data-access-shared-services';
import {
  EnvironmentService,
} from '@nomoh-monorepo/shared-data-access-environment';
import {
  ehPaisBrasil,
  EstadosConst,
  IPaisOption,
  listarPaises,
  normalizarCodigoPais,
  PAIS_BRASIL,
} from '@nomoh-monorepo/shared-types';
import {
  formatarCNPJ,
  formatarCPF,
  stringApenasNumeros,
  validarCNPJ,
  validarCPF,
} from '@nomoh-monorepo/shared-util-tools';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { InputMaskModule } from 'primeng/inputmask'; // mantido para máscara de CEP
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { BottomSheetComponent } from '../bottom-sheet/bottom-sheet.component';
import { DeviceService } from '../device/device.service';
import { FormFieldComponent } from '../form-field/form-field.component';
import { NFieldsetComponent } from '../n-fieldset/n-fieldset.component';
import { NMultiselectComponent } from '../n-multiselect/n-multiselect.component';
import { PhoneInputComponent } from '../phone-input/phone-input.component';

@Component({
  selector: 'lib-completar-cadastro-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    BottomSheetComponent,
    FormFieldComponent,
    NFieldsetComponent,
    MessageModule,
    InputTextModule,
    InputMaskModule, // mantido para máscara de CEP
    DatePickerModule,
    SelectModule,
    NMultiselectComponent,
    PhoneInputComponent,
  ],
  templateUrl: './completar-cadastro-modal.component.html',
  styleUrls: ['./completar-cadastro-modal.component.scss'],
})
export class CompletarCadastroModalComponent {
  readonly deviceService = inject(DeviceService);
  private auth = inject(AuthDataService);
  private fb = inject(FormBuilder);
  private pessoaData = inject(PessoaDataService);
  private servicoData = inject(ServicoDataService);
  private notificacao = inject(NotificacaoService);
  private http = inject(HttpClient);
  private environment = inject(EnvironmentService);
  private destroyRef = inject(DestroyRef);
  private paisAnterior = '';
  private ignorarLimpezaEndereco = true;

  visible = signal(false);
  saving = signal(false);
  erroServidor = signal<string | null>(null);
  $exibirCpf = signal(true);
  $enderecoBrasil = signal(true);
  form!: FormGroup;
  hoje = new Date();
  estados = EstadosConst.sigla;
  paises: IPaisOption[] = listarPaises();
  sexoOpcoes = [
    { label: 'Não informado', value: 0 },
    { label: 'Masculino', value: 1 },
    { label: 'Feminino', value: 2 },
  ];

  private sessionSkipKey = 'completar_cadastro_skipped';

  constructor() {
    this.form = this.fb.group({
      telefone: ['', Validators.required],
      cpfCnpj: ['', [this.cpfCnpjValidator]],
      dataNascimento: [null, Validators.required],
      sexo: [0],
      endereco: this.fb.group({
        pais: ['', Validators.required],
        codPostal: ['', [Validators.required, Validators.maxLength(20)]],
        logradouro: ['', Validators.required],
        numero: ['', Validators.required],
        complemento: [''],
        bairro: ['', [Validators.required, Validators.maxLength(100)]],
        cidade: ['', Validators.required],
        estado: ['', Validators.required],
      }),
    });

    const paisCtrl = this.form.get('endereco.pais')!;
    paisCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pais) => this.sincronizarPais(pais));
    this.sincronizarPais(paisCtrl.value);

    effect(() => {
      const user = this.auth.$user();
      const pending = this.auth.$completarCadastroPendente();
      const termoPendente = this.auth.$termosPendentes().length > 0;
      // Termo de aceite primeiro; completar cadastro só depois do aceite.
      if (
        user &&
        pending &&
        !termoPendente &&
        !sessionStorage.getItem(this.sessionSkipKey)
      ) {
        this.visible.set(true);
        void this.preencherFormulario();
      } else if (!pending || termoPendente) {
        this.visible.set(false);
      }
    });
  }

  get paisControl(): FormControl {
    return this.form.get('endereco.pais') as FormControl;
  }

  get dropdownAppendTo(): string | undefined {
    return this.deviceService.$isMobile() ? undefined : 'body';
  }

  private async preencherFormulario(): Promise<void> {
    this.ignorarLimpezaEndereco = true;
    try {
      await this.pessoaData.buscarPessoaLogada();
      await new Promise((r) => setTimeout(r, 300));
      const pessoa = this.pessoaData.$userInfo();
      if (!pessoa?.id) return;
      const paisSalvo = pessoa.endereco?.pais?.trim();
      this.form.patchValue({
        telefone: pessoa.telefone || '',
        cpfCnpj: pessoa.cpfCnpj || '',
        dataNascimento: pessoa.dataNascimento
          ? new Date(pessoa.dataNascimento)
          : null,
        sexo: pessoa.sexo ?? 0,
        endereco: {
          codPostal: pessoa.endereco?.codPostal || '',
          logradouro: pessoa.endereco?.logradouro || '',
          numero: pessoa.endereco?.numero || '',
          complemento: pessoa.endereco?.complemento || '',
          bairro: pessoa.endereco?.bairro || '',
          cidade: pessoa.endereco?.cidade || '',
          estado: pessoa.endereco?.estado || '',
          pais: paisSalvo ? normalizarCodigoPais(paisSalvo) : '',
        },
      });
      if (!paisSalvo) {
        await this.preencherPaisPorIp();
      }
    } catch {
      // ignore
    } finally {
      this.ignorarLimpezaEndereco = false;
    }
  }

  private async preencherPaisPorIp(): Promise<void> {
    const controle = this.form.get('endereco.pais');
    if (!controle || controle.value) {
      return;
    }
    const codigo = await this.servicoData.buscarPaisPorIp();
    if (controle.value) {
      return;
    }
    controle.setValue(codigo ?? PAIS_BRASIL);
  }

  private sincronizarPais(pais: unknown): void {
    const codigo = normalizarCodigoPais(typeof pais === 'string' ? pais : null);
    const brasil = codigo === PAIS_BRASIL;
    this.$exibirCpf.set(brasil);
    this.$enderecoBrasil.set(brasil);

    const cpf = this.form.get('cpfCnpj');
    if (brasil) {
      cpf?.enable({ emitEvent: false });
    } else {
      cpf?.disable({ emitEvent: false });
    }

    const mudouTipo = ehPaisBrasil(this.paisAnterior) !== brasil;
    this.paisAnterior = codigo;

    const endereco = this.form.get('endereco') as FormGroup;
    const bairro = endereco.get('bairro');
    const complemento = endereco.get('complemento');
    const estado = endereco.get('estado');
    const codPostal = endereco.get('codPostal');

    if (brasil) {
      bairro?.setValidators([Validators.required, Validators.maxLength(100)]);
      estado?.setValidators([Validators.required]);
      codPostal?.setValidators([Validators.required, Validators.maxLength(20)]);
    } else {
      bairro?.clearValidators();
      estado?.setValidators([Validators.required, Validators.maxLength(100)]);
      codPostal?.setValidators([Validators.required, Validators.maxLength(20)]);
    }

    if (!this.ignorarLimpezaEndereco && mudouTipo) {
      if (brasil) {
        if (!this.estados.includes(String(estado?.value ?? ''))) {
          estado?.setValue('', { emitEvent: false });
        }
        const digitos = String(codPostal?.value ?? '').replace(/\D/g, '');
        if (digitos.length !== 8) {
          codPostal?.setValue('', { emitEvent: false });
        }
        if (bairro?.value == null) {
          bairro?.setValue('', { emitEvent: false });
        }
      } else {
        bairro?.setValue(null, { emitEvent: false });
        complemento?.setValue(null, { emitEvent: false });
        if (this.estados.includes(String(estado?.value ?? ''))) {
          estado?.setValue('', { emitEvent: false });
        }
        const digitos = String(codPostal?.value ?? '').replace(/\D/g, '');
        if (digitos.length === 8) {
          codPostal?.setValue('', { emitEvent: false });
        }
      }
    }

    bairro?.updateValueAndValidity({ emitEvent: false });
    complemento?.updateValueAndValidity({ emitEvent: false });
    estado?.updateValueAndValidity({ emitEvent: false });
    codPostal?.updateValueAndValidity({ emitEvent: false });
  }

  cpfCnpjValidator(control: { value?: string }) {
    const valor = control.value;
    if (!valor) return null;
    const limpo = stringApenasNumeros(valor);
    if (!validarCPF(limpo) && !validarCNPJ(limpo)) {
      return { cpfCnpjInvalido: true };
    }
    return null;
  }

  onTelefoneChange(value: string): void {
    const ctrl = this.form.get('telefone');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }

  formatarCpfCnpj(event: Event) {
    const input = event.target as HTMLInputElement;
    const valor = stringApenasNumeros(input.value);
    input.value =
      valor.length <= 11 ? formatarCPF(valor) : formatarCNPJ(valor);
    this.form.get('cpfCnpj')?.setValue(input.value);
  }

  async buscarCep(event: Event) {
    const input = event.target as HTMLInputElement;
    const cep = stringApenasNumeros(input.value);
    if (cep.length !== 8) return;
    const data: any = await this.servicoData.buscarCep(cep);
    if (data && !data.erro) {
      this.form.patchValue({
        endereco: {
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || '',
          pais: PAIS_BRASIL,
        },
      });
    }
  }

  temEnderecoPreenchido(): boolean {
    const e = this.form.value.endereco;
    return !!(
      stringApenasNumeros(e?.codPostal || '') ||
      e?.logradouro ||
      e?.cidade
    );
  }

  onVisibleChange(visivel: boolean): void {
    this.visible.set(visivel);
    if (!visivel && this.auth.$completarCadastroPendente()) {
      this.pular();
    }
  }

  pular(): void {
    if (!this.auth.$completarCadastroPendente()) {
      this.visible.set(false);
      return;
    }
    if (!this.temEnderecoPreenchido()) {
      this.notificacao.notificacaoWarn(
        'Sem endereço você pode perder promoções baseadas na sua localidade.'
      );
    }
    sessionStorage.setItem(this.sessionSkipKey, '1');
    this.auth.clearCompletarCadastroPendente();
    this.visible.set(false);
  }

  async salvar(): Promise<void> {
    this.erroServidor.set(null);
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.notificacao.notificacaoWarn('Preencha os campos obrigatórios.');
      return;
    }

    this.saving.set(true);
    try {
      const v = this.form.getRawValue();
      const brasil = ehPaisBrasil(v.endereco.pais);
      const cpfCnpj = stringApenasNumeros(v.cpfCnpj || '');
      const body: Record<string, unknown> = {
        telefone: v.telefone || undefined,
        dataNascimento:
          v.dataNascimento instanceof Date
            ? v.dataNascimento.toISOString()
            : v.dataNascimento,
        sexo: v.sexo ?? 0,
        endereco: {
          logradouro: v.endereco.logradouro,
          numero: v.endereco.numero,
          cidade: v.endereco.cidade,
          estado: v.endereco.estado,
          pais: normalizarCodigoPais(v.endereco.pais),
          codPostal: brasil
            ? stringApenasNumeros(v.endereco.codPostal)
            : String(v.endereco.codPostal ?? '').trim(),
          bairro: brasil ? v.endereco.bairro : null,
          complemento: brasil ? v.endereco.complemento || '' : null,
        },
      };
      if (brasil && cpfCnpj) {
        body['cpfCnpj'] = cpfCnpj;
      }

      const url = `${this.environment.getApiUrl('nomoh')}/pessoa/completar-cadastro`;
      await firstValueFrom(this.http.patch(url, body));
      this.auth.clearCompletarCadastroPendente();
      sessionStorage.removeItem(this.sessionSkipKey);
      this.visible.set(false);
      await this.pessoaData.buscarPessoaLogada();
    } catch (erro: unknown) {
      const mensagem = mensagemHttp(erro);
      this.erroServidor.set(mensagem);
      if (mensagem) {
        this.notificacao.notificacaoWarn(mensagem);
      }
    } finally {
      this.saving.set(false);
    }
  }
}

function mensagemHttp(erro: unknown): string | null {
  const corpo = (erro as { error?: { message?: unknown } })?.error;
  const mensagem = corpo?.message;
  if (typeof mensagem === 'string' && mensagem.trim()) {
    return mensagem.trim();
  }
  return 'Não foi possível salvar o cadastro.';
}
